/**
 * Busca de usuário no cliente — mesmos prefixos da busca do servidor.
 *
 * Telas como a listagem de alunos filtram no servidor (`Search_Helpers::parse_search_term()`
 * em PHP). Já as abas Certificados e Notas carregam a turma inteira de uma vez e filtram em
 * memória — sem isto aqui elas só casavam pelo nome, e o mesmo texto digitado dava resultado
 * diferente dependendo da tela.
 *
 * ⚠️ Paridade: este parser espelha `Search_Helpers::parse_search_term()`
 * (includes/helpers/class-search-helpers.php). Mudou o contrato de prefixos lá? Mude aqui.
 * O tooltip que documenta os prefixos ao usuário é o componente `Search_Help` (PHP).
 *
 * Prefixos: `#123` / `id:123` (ID), `tel:numero`, `email:endereco` / `@endereco`;
 * número puro também casa com o ID; o resto é texto (nome ou e-mail).
 *
 * @package Labead
 */

/**
 * Interpreta o texto digitado.
 *
 * @param {string} raw Texto do campo de busca.
 * @returns {{type: string, value: (string|number), numericId: (number|null)}}
 */
function parse(raw) {
	const s = String(raw ?? '').trim();

	if (!s) return { type: 'text', value: '', numericId: null };

	let m = s.match(/^(?:#|id:)(\d+)$/i);
	if (m) return { type: 'id', value: parseInt(m[1], 10), numericId: null };

	m = s.match(/^tel:\s*(.+)$/i);
	if (m) return { type: 'tel', value: m[1].trim(), numericId: null };

	m = s.match(/^(?:email:|@)(.+)$/i);
	if (m) return { type: 'email', value: m[1].trim(), numericId: null };

	return { type: 'text', value: s, numericId: /^\d+$/.test(s) ? parseInt(s, 10) : null };
}

/** Só os dígitos — telefone é comparado sem máscara. */
function digits(value) {
	return String(value ?? '').replace(/\D+/g, '');
}

/**
 * Testa uma linha contra o termo já interpretado.
 *
 * A linha pode usar `id` ou `userId` (as duas abas nomeiam diferente) e precisa
 * ter `name`; `email`/`phone` são opcionais — sem eles o prefixo correspondente
 * simplesmente não casa, em vez de quebrar.
 *
 * @param {Object} row    Linha (id/userId, name, email, phone).
 * @param {Object} parsed Saída de parse().
 * @returns {boolean}
 */
function matches(row, parsed) {
	if (!parsed || parsed.value === '') return true;

	const id = Number(row.id ?? row.userId ?? 0);
	const name = String(row.name ?? '').toLowerCase();
	const email = String(row.email ?? '').toLowerCase();
	const phone = digits(row.phone);
	const value = String(parsed.value).toLowerCase();

	if (parsed.type === 'id') return id === parsed.value;

	if (parsed.type === 'tel') {
		const wanted = digits(parsed.value);
		return !!wanted && phone.includes(wanted);
	}

	if (parsed.type === 'email') return email.includes(value);

	// Texto: nome ou e-mail; número puro também casa com o ID.
	if (parsed.numericId !== null && id === parsed.numericId) return true;
	return name.includes(value) || email.includes(value);
}

/**
 * Filtra uma lista pelo texto digitado.
 *
 * @param {Array}  rows Linhas.
 * @param {string} raw  Texto do campo de busca.
 * @returns {Array}
 */
function filter(rows, raw) {
	const parsed = parse(raw);
	if (parsed.value === '') return Array.isArray(rows) ? rows.slice() : [];
	return (Array.isArray(rows) ? rows : []).filter((row) => matches(row, parsed));
}

window.lbUserSearch = { parse, matches, filter };

export { parse, matches, filter };
